import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ToastrNotificationService } from '../ui/toastr-notification.service';
import {
  CreatePlayerPayload,
  TransferredPlayer,
  Player,
  PlayerByCode,
  VerificationHistory,
  PlayerBlockHistory,
  QuickRegisterPlayerPayload,
} from '../../interfaces/api/player.interface';
import { ApiData, ApiMessage, ApiResponse } from '../../interfaces/api/api-response.interface';
import { map, Observable, tap } from 'rxjs';
import { NgSelect } from '../../interfaces/ui/ui.interface';
import { EncryptionService } from '../ui/encryption.service';

@Injectable({
  providedIn: 'root',
})
export class PlayerService {
  private apiUrl: string = environment.apiUrl;
  private path: string = '/player';
  private pathTransferred: string = '/transferred-player';

  constructor(
    private _httpClient: HttpClient,
    private _notificationService: ToastrNotificationService,
    private _encryptionService: EncryptionService
  ) {}

  getPlayerBlockHistory(
    playerId: string,
    filter: object
  ): Observable<ApiResponse<ApiData<PlayerBlockHistory[]>>> {
    const endpoint = `${this.apiUrl}${this.path}/block-history/${playerId}`;
    const cleanFilter = Object.fromEntries(
      Object.entries(filter).filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          !(Array.isArray(value) && value.length === 0)
      )
    );
    const params = new HttpParams({ fromObject: cleanFilter });
    return this._httpClient.get<ApiResponse<ApiData<PlayerBlockHistory[]>>>(endpoint, {
      params,
    });
  }

  getAllBlockedPlayersFilter(filter: object): Observable<ApiResponse<ApiData<Player[]>>> {
    const endpoint = `${this.apiUrl}${this.path}/find-all-blocked-players-filters`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<Player[]>>>(endpoint, {
      params,
    });
  }

  getAllTransferredPlayersFilter(filter: object): Observable<ApiResponse<ApiData<Player[]>>> {
    const endpoint = `${this.apiUrl}${this.pathTransferred}/find-all-transferred-players`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<Player[]>>>(endpoint, {
      params,
    });
  }

  getPlayersFilter(filters?: any): Observable<ApiResponse<ApiData<Player[]>>> {
    const endpoint = `${this.apiUrl}${this.path}/get-all`;
    return this._httpClient.get<ApiResponse<ApiData<Player[]>>>(endpoint);
  }

  findPlayerByCode(code: string): Observable<ApiResponse<PlayerByCode>> {
    const endpoint = `${this.apiUrl}${this.path}/find-by-code`;
    const params = new HttpParams({ fromObject: { code } });
    return this._httpClient.get<ApiResponse<PlayerByCode>>(endpoint, { params });
  }

  getPlayerById(id: string): Observable<ApiResponse<Player>> {
    const payload = { id: id };
    const jsonString = JSON.stringify(payload);
    const encryptedData = this._encryptionService.encrypt(jsonString);

    const endpoint = `${this.apiUrl}${this.path}/get-by-id`;

    return this._httpClient.post<ApiResponse<Player>>(endpoint, {
      data: encryptedData,
    });
  }

  getAllPlayersFilters(filter: object): Observable<ApiResponse<ApiData<Player[]>>> {
    const endpoint = `${this.apiUrl}${this.path}/find-all-players`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<Player[]>>>(endpoint, {
      params,
    });
  }

  public findPlayersForSelect(filter: object): Observable<ApiData<NgSelect<string>[]>> {
    return this.getAllActivePlayerBySelect(filter).pipe(
      map((response) => ({
        result: response.data.result.map((user) => ({
          label: user.personalInfo.username,
          value: user._id,
          id: user.accountInfo.playerId,
          labelCombined: `${user.accountInfo.playerId} - ${user.personalInfo.username}`,
          balance: user.accountInfo.balance,
          currencyCode: user.accountInfo.currencyId.code,
          currencyId: user.accountInfo.currencyId._id,
        })),
        totalCount: response.data.totalCount,
      }))
    );
  }

  public findPlayersSimpleForSelect(filter: object): Observable<ApiData<NgSelect<string>[]>> {
    return this.getAllActivePlayerBySelect(filter).pipe(
      map((response) => ({
        result: response.data.result.map((user) => ({
          label: user.personalInfo.username,
          value: user._id,
        })),
        totalCount: response.data.totalCount,
      }))
    );
  }

  /**
   * Options whose `value` is the PLAYER ID, not the Mongo `_id`.
   *
   * The other `...ForSelect` variants bind `_id`, which is right for endpoints
   * that take an ObjectId. Journey conditions compare against
   * `player.accountInfo.playerId`, so an `_id` there would never match. The id
   * is passed through untouched — the backend compares it against the payload
   * value as-is, so converting it to a string here would break the comparison.
   */
  public findPlayersByPlayerIdForSelect(
    filter: object
  ): Observable<ApiData<NgSelect<number>[]>> {
    return this.getAllActivePlayerBySelect(filter).pipe(
      map((response) => ({
        result: response.data.result.map((user) => ({
          label: `${user.accountInfo.playerId} - ${user.personalInfo.username}`,
          value: user.accountInfo.playerId,
        })),
        totalCount: response.data.totalCount,
      }))
    );
  }

  getAllActivePlayerBySelect(filter: object): Observable<ApiResponse<ApiData<Player[]>>> {
    const endpoint = `${this.apiUrl}${this.path}/get-active-players-by-select`;
    const params = new HttpParams({
      fromObject: { ...filter, from: 'filter' },
    });
    return this._httpClient.get<ApiResponse<ApiData<Player[]>>>(endpoint, {
      params,
    });
  }

  getAllActivePlayersFilters(filter: object): Observable<ApiResponse<ApiData<Player[]>>> {
    const endpoint = `${this.apiUrl}${this.path}/find-all-active-players-filters`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<Player[]>>>(endpoint, {
      params,
    });
  }

  getTotalCountPlayers(): Observable<ApiResponse<ApiData<[]>>> {
    const endpoint = `${this.apiUrl}${this.path}/get-total-players`;
    return this._httpClient.get<ApiResponse<ApiData<[]>>>(endpoint);
  }

  updatePassword(request: {
    playerId: string | string[];
    password: string;
    isRequiredChangePassword: boolean;
  }): Observable<ApiResponse<ApiData<Player>>> {
    const endpoint = `${this.apiUrl}${this.path}/update-password`;
    return this._httpClient
      .put<ApiResponse<ApiData<Player>>>(endpoint, request)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  updatePlayer(request: object): Observable<ApiResponse<ApiData<Player>>> {
    const endpoint = `${this.apiUrl}${this.path}/update-player`;
    return this._httpClient
      .put<ApiResponse<ApiData<Player>>>(endpoint, request)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  updatePlayerPermission(request: object): Observable<ApiResponse<ApiData<Player>>> {
    const endpoint = `${this.apiUrl}${this.path}/update-page-permissions`;
    return this._httpClient
      .put<ApiResponse<ApiData<Player>>>(endpoint, request)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  setPlayerParent = (request: TransferredPlayer): Observable<ApiResponse<ApiData<Player>>> => {
    const endpoint = `${this.apiUrl}${this.path}/set-player-parent`;
    return this._httpClient
      .put<ApiResponse<ApiData<Player>>>(endpoint, request)
      .pipe(tap((res) => this.showNotification(res.message)));
  };

  setPlayerStatus = (request: {
    playerId: string | string[];
    state: string;
    reason?: string;
    blockedById?: string;
  }): Observable<ApiResponse<ApiData<Player>>> => {
    const endpoint = `${this.apiUrl}${this.path}/set-player-status`;
    return this._httpClient
      .put<ApiResponse<ApiData<Player>>>(endpoint, request)
      .pipe(tap((res) => this.showNotification(res.message)));
  };

  createPlayer(player: CreatePlayerPayload): Observable<ApiResponse<ApiData<Player>>> {
    const endpoint = `${this.apiUrl}${this.path}/register`;
    return this._httpClient
      .post<ApiResponse<ApiData<Player>>>(endpoint, player)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  quickRegisterPlayer(payload: QuickRegisterPlayerPayload): Observable<ApiResponse<ApiData<Player>>> {
    const endpoint = `${this.apiUrl}${this.path}/quick-register`;
    return this._httpClient
      .post<ApiResponse<ApiData<Player>>>(endpoint, payload)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  getPlayersByAgentId(
    agentId: string,
    page: number = 1,
    limit: number = 10,
    username?: string
  ): Observable<ApiResponse<ApiData<Player[]>>> {
    const endpoint = `${this.apiUrl}${this.path}/get-players-by-agent`;
    let params = new HttpParams()
      .set('agentId', agentId)
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (username) {
      params = params.set('username', username);
    }

    return this._httpClient.get<ApiResponse<ApiData<Player[]>>>(endpoint, {
      params,
    });
  }

  public findVerificationRequestsForSelect(
    filter: object
  ): Observable<ApiData<NgSelect<string>[]>> {
    return this.getVerificationRequests(filter).pipe(
      map((response) => ({
        result: response.data.result.map((user) => ({
          label: user.personalInfo.username,
          value: user._id,
        })),
        totalCount: response.data.totalCount,
      }))
    );
  }

  public getVerificationRequests(filter: object = {}): Observable<ApiResponse<ApiData<Player[]>>> {
    const endpoint = `${this.apiUrl}${this.path}/get-verification-requests`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<Player[]>>>(endpoint, { params });
  }

  public verifyPlayer(data: object): Observable<ApiResponse<ApiData<Player>>> {
    const endpoint = `${this.apiUrl}${this.path}/verify-player`;
    return this._httpClient
      .post<ApiResponse<ApiData<Player>>>(endpoint, data)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  public rejectVerification(data: object): Observable<ApiResponse<ApiData<Player>>> {
    const endpoint = `${this.apiUrl}${this.path}/reject-verification`;
    return this._httpClient
      .post<ApiResponse<ApiData<Player>>>(endpoint, data)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  public getVerificationHistory(playerId: string): Observable<ApiResponse<VerificationHistory[]>> {
    const endpoint = `${this.apiUrl}${this.path}/get-verification-history/${playerId}`;
    return this._httpClient.get<ApiResponse<VerificationHistory[]>>(endpoint);
  }

  private showNotification(message: ApiMessage): void {
    this._notificationService.showNotification({
      title: 'players.player.title',
      message: message,
      type: 'success',
    });
  }
}
